import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Button, Card, Form, Input, InputNumber, Modal, Select, Space, Table, Tag, Typography,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import { formatDateEuropean } from '../utils/dateUtils';
import { warningCopy } from '../modules/operationalExceptions/operationalExceptionText';
import {
  OPERATIONAL_EXCEPTION_CODES,
  fetchConsistencyWarningSettings,
  fetchOperationalExceptions,
  operationalExceptionDocumentPath,
  resolveOperationalException,
  saveConsistencyWarningSettings,
  type OperationalExceptionDto,
  type OperationalExceptionStatusFilter,
} from '../modules/operationalExceptions/operationalExceptionsApi';

/**
 * The registry shows the very sentence the operator confirmed in the dialog:
 * both read `operationalExceptionText.ts`. A code this build does not know
 * still reads as a sentence, with the raw code only as a grey caption.
 */
const ExceptionDetails: React.FC<{ row: OperationalExceptionDto; t: TFunction }> = ({ row, t }) => {
  const copy = warningCopy({ code: row.warningCode, details: row.details }, t);
  return (
    <Space direction="vertical" size={0}>
      <Typography.Text style={{ whiteSpace: 'pre-line' }}>{copy.text}</Typography.Text>
      {copy.supportCode && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('common:operationalException.supportCode', { code: copy.supportCode })}
        </Typography.Text>
      )}
    </Space>
  );
};

/**
 * Administrator list of confirmed operational exceptions (decision
 * 2026-09-18): every warning the operator continued through, with the
 * reason, author and document link. An entry stays "open" until an
 * administrator marks it as explained with a comment.
 */
// WP2: WP1 "needs re-settlement" entries. A correction invoice closes them;
// the administrator can close one by hand when no correction is needed.
const RESETTLEMENT_CODES = new Set([
  'ORDER_CHANGED_AFTER_INVOICE',
  'WZ_CORRECTED_AFTER_INVOICE',
  'SUPPLIER_ORDER_CHANGED_AFTER_INVOICE',
  'SUPPLIER_ORDER_PZ_QUANTITY_REDUCED',
  'ORDER_ISSUED_OVER_INVOICE',
]);

const OperationalExceptions: React.FC = () => {
  const { t } = useTranslation(['common']);
  const feedback = useFeedback();
  const [rows, setRows] = useState<OperationalExceptionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<OperationalExceptionStatusFilter>('open');
  const [warningCode, setWarningCode] = useState<string | null>(null);
  const [resolving, setResolving] = useState<OperationalExceptionDto | null>(null);
  const [resolveSubmitting, setResolveSubmitting] = useState(false);
  const [resolveForm] = Form.useForm<{ comment: string }>();
  const [threshold, setThreshold] = useState<number | null>(null);
  const [thresholdSaving, setThresholdSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await fetchOperationalExceptions({ status, warningCode }));
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'common:operationalExceptionsAdmin.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [status, warningCode, feedback, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetchConsistencyWarningSettings()
      .then((settings) => setThreshold(settings.purchasePriceDeviationPercent))
      .catch((error) => feedback.error(getApiErrorMessage(error, t, 'common:operationalExceptionsAdmin.settings.fetchError')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const codeLabel = (code: string) => {
    const key = `common:operationalExceptionsAdmin.codes.${code}`;
    const label = t(key);
    return label === key ? code : label;
  };

  const handleResolve = async () => {
    if (!resolving) return;
    try {
      const values = await resolveForm.validateFields();
      setResolveSubmitting(true);
      await resolveOperationalException(resolving.id, values.comment.trim());
      feedback.success(t('common:operationalExceptionsAdmin.resolveSuccess'));
      setResolving(null);
      resolveForm.resetFields();
      await load();
    } catch (error: any) {
      if (error?.errorFields) return;
      feedback.error(getApiErrorMessage(error, t, 'common:operationalExceptionsAdmin.resolveError'));
    } finally {
      setResolveSubmitting(false);
    }
  };

  const handleSaveThreshold = async () => {
    if (threshold === null) return;
    setThresholdSaving(true);
    try {
      const saved = await saveConsistencyWarningSettings(threshold);
      setThreshold(saved.purchasePriceDeviationPercent);
      feedback.success(t('common:operationalExceptionsAdmin.settings.saveSuccess', { percent: saved.purchasePriceDeviationPercent }));
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'common:operationalExceptionsAdmin.settings.saveError'));
    } finally {
      setThresholdSaving(false);
    }
  };

  const columns: TableColumnsType<OperationalExceptionDto> = [
    {
      title: t('common:operationalExceptionsAdmin.columns.createdAt'),
      dataIndex: 'createdAt',
      width: 120,
      render: (value: string) => formatDateEuropean(value),
    },
    {
      title: t('common:operationalExceptionsAdmin.columns.code'),
      dataIndex: 'warningCode',
      width: 220,
      render: (code: string) => <Tag color="gold">{codeLabel(code)}</Tag>,
    },
    {
      title: t('common:operationalExceptionsAdmin.columns.document'),
      key: 'document',
      width: 180,
      render: (_: unknown, row) => {
        const path = operationalExceptionDocumentPath(row);
        const label = t(`common:operationalExceptionsAdmin.scopes.${row.scopeType}`);
        return path
          ? <Link to={path}>{label} — {t('common:operationalExceptionsAdmin.openDocument')}</Link>
          : label;
      },
    },
    {
      title: t('common:operationalExceptionsAdmin.columns.details'),
      key: 'details',
      render: (_: unknown, row) => (
        <ExceptionDetails row={row} t={t} />
      ),
    },
    {
      title: t('common:operationalExceptionsAdmin.columns.reason'),
      key: 'reason',
      width: 260,
      render: (_: unknown, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{row.reason}</Typography.Text>
          <Typography.Text type="secondary">{row.confirmedByName}</Typography.Text>
        </Space>
      ),
    },
    {
      title: t('common:operationalExceptionsAdmin.columns.resolution'),
      key: 'resolution',
      width: 240,
      render: (_: unknown, row) => (row.resolutionId ? (
        <Space direction="vertical" size={0}>
          {row.settledByInvoice && <Tag color="green">
            {t('common:operationalExceptionsAdmin.settledByCorrection', { number: row.settledByInvoiceNumber || '' })}
          </Tag>}
          <Typography.Text>{row.resolutionComment}</Typography.Text>
          <Typography.Text type="secondary">
            {t('common:operationalExceptionsAdmin.resolvedBy', {
              name: row.resolvedByName || '',
              date: formatDateEuropean(row.resolvedAt),
            })}
          </Typography.Text>
        </Space>
      ) : (
        <Button size="small" onClick={() => { resolveForm.resetFields(); setResolving(row); }}>
          {t('common:operationalExceptionsAdmin.resolve')}
        </Button>
      )),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title={t('common:operationalExceptionsAdmin.title')}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert type="info" showIcon message={t('common:operationalExceptionsAdmin.description')} />
          <Space wrap>
            <Select<OperationalExceptionStatusFilter>
              value={status}
              onChange={setStatus}
              style={{ width: 160 }}
              options={[
                { value: 'open', label: t('common:operationalExceptionsAdmin.filters.open') },
                { value: 'resolved', label: t('common:operationalExceptionsAdmin.filters.resolved') },
                { value: 'all', label: t('common:operationalExceptionsAdmin.filters.all') },
              ]}
              aria-label={t('common:operationalExceptionsAdmin.filters.status')}
            />
            <Select<string | null>
              value={warningCode}
              onChange={(value) => setWarningCode(value || null)}
              allowClear
              placeholder={t('common:operationalExceptionsAdmin.filters.allCodes')}
              style={{ width: 320 }}
              options={OPERATIONAL_EXCEPTION_CODES.map((code) => ({ value: code, label: codeLabel(code) }))}
              aria-label={t('common:operationalExceptionsAdmin.filters.code')}
            />
          </Space>
          <Table<OperationalExceptionDto>
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 25 }}
            scroll={{ x: 1100 }}
            locale={{ emptyText: t('common:operationalExceptionsAdmin.empty') }}
          />
        </Space>
      </Card>

      <Card title={t('common:operationalExceptionsAdmin.settings.title')} style={{ maxWidth: 640 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {t('common:operationalExceptionsAdmin.settings.description')}
          </Typography.Paragraph>
          <Space>
            <Typography.Text>{t('common:operationalExceptionsAdmin.settings.threshold')}</Typography.Text>
            <InputNumber
              min={0}
              max={1000}
              value={threshold}
              onChange={(value) => setThreshold(typeof value === 'number' ? value : null)}
              aria-label={t('common:operationalExceptionsAdmin.settings.threshold')}
            />
            <Button type="primary" loading={thresholdSaving} disabled={threshold === null} onClick={handleSaveThreshold}>
              {t('common:operationalExceptionsAdmin.settings.save')}
            </Button>
          </Space>
        </Space>
      </Card>

      <Modal
        open={Boolean(resolving)}
        title={t('common:operationalExceptionsAdmin.resolveTitle')}
        okText={t('common:operationalExceptionsAdmin.resolve')}
        cancelText={t('common:cancel')}
        confirmLoading={resolveSubmitting}
        onOk={handleResolve}
        onCancel={() => { setResolving(null); resolveForm.resetFields(); }}
        destroyOnClose
      >
        <Form form={resolveForm} layout="vertical">
          <Form.Item
            name="comment"
            label={t('common:operationalExceptionsAdmin.resolveComment')}
            rules={[{ required: true, whitespace: true, message: t('common:operationalExceptionsAdmin.resolveCommentRequired') }]}
          >
            <Input.TextArea rows={3} maxLength={500} showCount placeholder={t('common:operationalExceptionsAdmin.resolveCommentPlaceholder')} />
          </Form.Item>
          {resolving && RESETTLEMENT_CODES.has(resolving.warningCode) && (
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">{t('common:operationalExceptionsAdmin.resettlementHint')}</Typography.Text>
              <Button size="small" onClick={() => resolveForm.setFieldsValue({ comment: t('common:operationalExceptionsAdmin.noCorrectionNeeded') })}>
                {t('common:operationalExceptionsAdmin.noCorrectionNeeded')}
              </Button>
            </Space>
          )}
        </Form>
      </Modal>
    </Space>
  );
};

export default OperationalExceptions;
